/**
 * Which stages a gardener may actually choose.
 *
 * Two of the catalogue's rows are bookkeeping rather than stages anyone is
 * in: Planning is before the plant exists, and All is a filter pretending to
 * be a stage. Neither is a thing to mark a photograph with. Finishing (stage
 * 100, stage_type EXPIRED) is a stage a plant is in at the end, so it is
 * offered like the rest; it is also the stage the plants list gives a plant
 * whose timeline has run out.
 *
 * Every stage's name comes from the server, said in the reader's language
 * (GET /stages and GET /plants; bloom-backend services/stages/stageKinds:
 * the todo_word row stage.<TYPE>, so Last Harvest is "Harvesting", or the
 * gardener's own name where they renamed it); the app prints it as sent and
 * names no stage itself (batch 10b of the architecture audit, A63). Adding a
 * plant offers Planning too, for a plant not yet sown (selectableStages,
 * planning).
 *
 * The rule lived twice — once in the tray, once in the profile's stage editor
 * — and not at all in the photo picker on the plants page, which offered all
 * three beside the gardener's own. One list, read by everything that asks
 * somebody to pick.
 *
 * Stages a gardener invented carry no stage_type at all, so they pass.
 *
 * @module stages
 */

import type { Stage } from '@/shared/types/stage';

/** Catalogue rows that exist for the software's sake, not the gardener's. */
const INTERNAL_STAGE_TYPES = new Set(['PLANNING', 'ALL']);

/**
 * The stages worth offering, in the order they arrived.
 *
 * @param stages - Everything the API returned, or nothing yet.
 * @param options - planning: offer Planning too (adding a plant: one still to
 *   be sown is Planning, as its card then says).
 * @returns The stages a gardener may pick from.
 */
export function selectableStages(stages: Stage[] | null | undefined, { planning = false }: { planning?: boolean } = {}): Stage[] {
  return (stages ?? []).filter((stage) => {
    const type = String(stage.stage_type);
    return !INTERNAL_STAGE_TYPES.has(type) || (planning && type === 'PLANNING');
  });
}
